import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Seo } from "@/components/Seo";
import DOMPurify from "dompurify";
import { fmtDate, publicGet } from "@/lib/api";
import { toHtml } from "@/lib/blogHtml";

const COVER = "https://static.prod-images.emergentagent.com/jobs/8a990b7d-87bf-4959-9ab4-9392f63d36b3/images/56fdd763cb546969e4ae5477ded8e26d664069c17f330fb9cd98ac7963395892.jpeg";

const abs = (u) => (u && u.startsWith("/") ? window.location.origin + u : u);

function Body({ text }) {
  const html = DOMPurify.sanitize(toHtml(text), { ADD_ATTR: ["target"] });
  return <div data-testid="blog-post-body" className="blog-content mt-8" dangerouslySetInnerHTML={{ __html: html }} />;
}

export function BlogPost() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { publicGet(`/blog/${slug}`).then(setP).catch(() => setErr(true)); }, [slug]);
  if (err) return <p className="py-24 text-center text-slate-400">Articolo non trovato.</p>;
  if (!p) return <p className="py-24 text-center text-slate-500">Caricamento…</p>;
  return (
    <article className="mx-auto max-w-3xl px-4 py-14 sm:px-6" data-testid="blog-post">
      <Seo title={p.seo_title || p.title} description={p.meta_description || p.excerpt} image={abs(p.cover_url) || COVER} type="article"
        jsonLd={{ "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title, description: p.meta_description || p.excerpt, datePublished: p.created_at,
          dateModified: p.updated_at, image: abs(p.cover_url) || COVER, keywords: p.focus_keyword || undefined, articleSection: p.category,
          author: { "@type": "Organization", name: "Club Prezzi" }, publisher: { "@type": "Organization", name: "Club Prezzi" },
          mainEntityOfPage: window.location.href, inLanguage: "it-IT" }} />
      <Link to="/blog" className="text-sm text-amber-400 hover:underline">← Tutti gli articoli</Link>
      <p className="mt-6 text-xs uppercase tracking-widest text-slate-500">{p.category} · {fmtDate(p.created_at)}</p>
      <h1 className="mt-2 font-display text-4xl font-extrabold leading-tight text-white">{p.title}</h1>
      <p className="mt-4 text-lg text-slate-400">{p.excerpt}</p>
      {p.cover_url && <img src={p.cover_url} alt={p.cover_alt || p.title} className="mt-8 w-full rounded-3xl border border-white/10" />}
      <Body text={p.content} />
    </article>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { publicGet("/blog").then(setPosts).catch(() => {}); }, []);
  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="blog-page">
      <Seo title="Blog: guide al risparmio, punti e offerte" description="Guide per confrontare i prezzi online, usare i punti del club e scegliere l'elettronica giusta al prezzo migliore." />
      <p className="font-club text-xs text-amber-400">BLOG</p>
      <h1 className="mt-2 font-display text-4xl font-extrabold text-white sm:text-5xl">Guide e consigli per risparmiare</h1>
      <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((p) => (
          <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group overflow-hidden rounded-3xl border border-white/10 bg-[#12141C] transition-transform duration-300 hover:-translate-y-1">
            <img src={p.cover_url || COVER} alt={p.cover_alt || p.title} loading="lazy" className="aspect-[16/9] w-full object-cover" />
            <div className="p-5">
              <p className="text-xs uppercase tracking-widest text-amber-400">{p.category} · {fmtDate(p.created_at)}</p>
              <h2 className="mt-2 font-display text-lg font-semibold text-white group-hover:text-amber-200">{p.title}</h2>
              <p className="mt-2 line-clamp-3 text-sm text-slate-400">{p.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
